import { AlertTriangle, CheckCircle2, LandmarkIcon } from "lucide-react";
import {
  useBankAccountsQuery,
  useDeleteAllConnectedPlaidAccountsMutation,
} from "src/apps/PostFundedDashboard/queries";
import { useActiveApplicantApplicationQuery } from "src/hooks/useApplication";

import { AssetList } from "src/components/AssetList";
import { BankAccountList } from "src/components/BankAccountList";
import { DebtList } from "src/components/DebtList";
import { Button } from "src/components/ui/button";
import { useConfirmBeforeRun } from "src/hooks/useConfirmBeforeRun";
import { usePlaidForApplication } from "src/hooks/usePlaid/usePlaidForApplication";
import PlaidLogo from "src/icons/plaid-logo.svg?react";

const IncomeVerify = () => {
  const { app } = useActiveApplicantApplicationQuery({ initialRefetch: false });

  const {
    start,
    ready,
    isLoading,
    registerMutation,
    bankStatements,
    isLoadingBankStatements,
    assets,
    debts,
    incomes,
    isLoadingApp,
  } = usePlaidForApplication({
    applicationId: app?.id,
    purpose: "underwriting",
    modal: false,
  });
  const { data: bankAccounts, isLoading: isLoadingBankAccounts } = useBankAccountsQuery();

  const deleteAllConnectionsFromPlaid = useDeleteAllConnectedPlaidAccountsMutation();

  const confirmDeleteAllConnectionsFromPlaid = useConfirmBeforeRun({
    fn: deleteAllConnectionsFromPlaid.mutateAsync,
    message: (
      <>
        <h1 className="font-semibold text-xl mb-2">Delete all accounts connected via Plaid?</h1>
        <p>
          Are you sure you want to delete <b>all</b> accounts that were connected via Plaid?
        </p>
        <p className="mt-2">
          <b>This action cannot be undone!</b> You will have to re-connect your accounts if you'd like to use
          Plaid again.
        </p>
      </>
    ),
    loading: deleteAllConnectionsFromPlaid.isPending,
    confirmType: "urgent",
  });

  const hasConnectedAccount = bankAccounts?.length || debts?.length || assets?.length || incomes?.length;

  return (
    <div className="flex flex-col p-6 gap-y-4">
      <div className="flex flex-row justify-between items-center gap-x-14 gap-y-4 flex-wrap">
        <div className="flex-1 md:min-w-[300px]">
          <p className="font-semibold text-3xl mb-1">Connect a bank account</p>
          <p>
            Connect your bank account to automatically pull statements and verify your income using{" "}
            <a
              className="text-green font-semibold inline-block"
              href="https://plaid.com"
              target="_blank"
              rel="noreferrer"
            >
              <PlaidLogo width={16} height={16} className="inline-block mb-1" /> Plaid.
            </a>
          </p>
        </div>

        <Button
          className="self-start"
          pendingWithIcon={isLoading}
          onClick={() => start()}
          disabled={isLoading || !ready}
        >
          {registerMutation?.data ? (
            <CheckCircle2 height={16} width={16} className="text-white mr-2" />
          ) : null}
          {registerMutation?.data ? "Bank connected!" : "Connect a bank account"}
          <LandmarkIcon height={16} width={16} className="ml-2" />
        </Button>
      </div>

      <div>
        {hasConnectedAccount ? (
          <div className="text-sm text-red-600 bg-red-50 border px-4 py-3 rounded-md flex flex-row items-center justify-between mb-4">
            <div className="flex flex-row items-center gap-3">
              <AlertTriangle className="inline-block min-w-5" />
              <p>
                Don't want this information connected anymore? You can delete all accounts connected via
                Plaid.
              </p>
            </div>
            <Button
              onClick={() => confirmDeleteAllConnectionsFromPlaid()}
              className="text-red-600 hover:bg-red-150"
              variant="ghost:red"
              loading={deleteAllConnectionsFromPlaid.isPending}
            >
              Remove Plaid connection
            </Button>
          </div>
        ) : null}

        <BankAccountList
          applicationId={app?.id}
          incomesLoading={isLoadingApp}
          bankAccountsLoading={isLoadingBankAccounts}
          bankStatementsLoading={isLoadingBankStatements}
          pendingConnect={isLoading}
          onClickConnect={() => start()}
          bankAccounts={bankAccounts}
          incomes={incomes}
          bankStatements={bankStatements}
        />
      </div>
      <div>
        {debts?.length ? <p className="font-semibold text-2xl mb-2">Your debts</p> : null}
        <DebtList applicationId={app?.id} debts={debts} />
      </div>
      <div>
        {debts?.length ? <p className="font-semibold text-2xl mb-2">Your other assets</p> : null}
        <AssetList applicationId={app?.id} assets={assets} />
      </div>
    </div>
  );
};

export default IncomeVerify;
